import React, { useMemo } from 'react';
import { Link as RouterLink, useLocation } from 'react-router';
import { Breadcrumbs, Link, Typography } from '@mui/material';
import { useMatch, useTournament } from '@/hooks/useApi';
import type { Player, Team } from '@/types';
import {
  parseTournamentListSearch,
  tournamentListPath,
  TOURNAMENT_LIST_TAB_LABELS,
} from '@/utils/tournamentListSearch';
import { getSportOption } from '@/utils/sports';

const SEGMENT_LABELS: Record<string, string> = {
  tournaments: 'Турниры',
  teams: 'Команды',
  players: 'Игроки',
  matches: 'Матчи',
  sports: 'Виды спорта',
  organizations: 'Организации',
  venues: 'Площадки',
  officials: 'Судьи',
  analytics: 'Аналитика',
  settings: 'Настройки',
  profile: 'Профиль',
  create: 'Создание',
  edit: 'Редактирование',
  stats: 'Статистика',
  results: 'Результаты',
  register: 'Регистрация',
  search: 'Поиск',
  new: 'Создание',
  live: 'Ведение матча',
};

const ENTITY_LABEL_BY_PARENT: Record<string, string> = {
  tournaments: 'Турнир',
  teams: 'Команда',
  players: 'Игрок',
  matches: 'Матч',
};

const LIST_PATHS = new Set([
  '/tournaments',
  '/teams',
  '/players',
  '/sports',
  '/organizations',
  '/venues',
  '/officials',
  '/analytics',
  '/settings',
  '/profile',
]);

function labelForSegment(segment: string, parent?: string): string {
  return SEGMENT_LABELS[segment] ?? (parent ? ENTITY_LABEL_BY_PARENT[parent] : undefined) ?? segment;
}

function isEntityId(segment: string, parent?: string): boolean {
  return Boolean(parent && ENTITY_LABEL_BY_PARENT[parent] && !SEGMENT_LABELS[segment]);
}

type Crumb = { to: string; label: string };

const MATCH_PATH = /^\/matches\/([^/]+)(\/results)?$/;
const TOURNAMENT_ID_PATH = /^\/tournaments\/(?!(?:create|new)(?:\/|$))([^/]+)/;

function participantName(
  participants: (Team | Player)[] | undefined,
  id?: string | null
): string {
  if (!id) return 'TBD';
  const participant = participants?.find((item) => item.id === id);
  if (!participant) return 'TBD';
  if ('firstName' in participant) {
    return `${participant.firstName} ${participant.lastName || ''}`.trim();
  }
  return participant.name;
}

function matchTitle(
  match?: { homeId?: string | null; awayId?: string | null; participants?: (Team | Player)[] },
  tournamentParticipants?: (Team | Player)[]
): string {
  if (!match) return 'Матч';
  const participants = match.participants?.length ? match.participants : tournamentParticipants;
  const home = participantName(participants, match.homeId);
  const away = participantName(participants, match.awayId);
  if (home === 'TBD' && away === 'TBD') return 'Матч';
  return `${home} vs ${away}`;
}

export const PageBreadcrumbs: React.FC = () => {
  const { pathname, search } = useLocation();
  const matchPath = pathname.match(MATCH_PATH);
  const matchId = matchPath?.[1] ?? '';
  const isResults = Boolean(matchPath?.[2]);
  const pathTournamentId = pathname.match(TOURNAMENT_ID_PATH)?.[1] ?? '';

  const { data: match } = useMatch(matchId);
  const tournamentId = pathTournamentId || match?.tournamentId || '';
  const { data: tournament } = useTournament(tournamentId, {
    enabled: Boolean(tournamentId),
  });
  const tournamentLabel = tournament?.name || 'Турнир';
  const matchLabel = matchTitle(match, tournament?.participants);

  const crumbs = useMemo(() => {
    if (pathname === '/tournaments') {
      const filters = parseTournamentListSearch(new URLSearchParams(search));
      const items: Crumb[] = [{ to: '/tournaments', label: 'Турниры' }];
      if (filters.sportType) {
        const sportLabel = getSportOption(filters.sportType)?.label;
        if (sportLabel) {
          items.push({
            to: tournamentListPath({ sportType: filters.sportType }),
            label: sportLabel,
          });
        }
      }
      if (filters.status) {
        items.push({
          to: tournamentListPath(filters),
          label: TOURNAMENT_LIST_TAB_LABELS[filters.status],
        });
      }
      return items;
    }

    if (matchId) {
      const items: Crumb[] = [{ to: '/tournaments', label: 'Турниры' }];
      if (match?.tournamentId) {
        items.push({
          to: `/tournaments/${match.tournamentId}`,
          label: tournamentLabel,
        });
      }
      items.push({ to: `/matches/${matchId}`, label: matchLabel });
      if (isResults) {
        items.push({ to: pathname, label: 'Результаты' });
      }
      return items;
    }

    const segments = pathname.split('/').filter(Boolean);
    let acc = '';
    return segments
      .map((segment, index) => {
        acc += `/${segment}`;
        const parent = segments[index - 1];
        const isTournament = isEntityId(segment, parent) && parent === 'tournaments';
        return {
          to: acc,
          label: isTournament ? tournamentLabel : labelForSegment(segment, parent),
          linkable: LIST_PATHS.has(acc) || isEntityId(segment, parent),
        };
      })
      .filter((crumb, index, list) => index === list.length - 1 || crumb.linkable);
  }, [isResults, match?.tournamentId, matchId, matchLabel, pathname, search, tournamentLabel]);

  if (pathname === '/') return null;

  return (
    <Breadcrumbs aria-label="breadcrumb" sx={{ px: 2, pt: 1.5, pb: 1.5 }}>
      <Link component={RouterLink} to="/" underline="hover" color="inherit">
        Главная
      </Link>
      {crumbs.map((crumb, index) =>
        index === crumbs.length - 1 ? (
          <Typography key={crumb.to} color="text.primary">
            {crumb.label}
          </Typography>
        ) : (
          <Link
            key={crumb.to}
            component={RouterLink}
            to={crumb.to}
            underline="hover"
            color="inherit"
          >
            {crumb.label}
          </Link>
        )
      )}
    </Breadcrumbs>
  );
};
